import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import {
    ExpenseType,
    ExpenseTypeEnum,
} from "@project/domain/models/expenses/expense-type.model";
import { ExpenseSubTypeEnum } from "@project/domain/models/expenses/sub-type.model";
import { ExpenseAllTypes } from "@project/domain/models/expenses/expense.model";
import { GetAllProjectExpenseTypesUseCase } from "@project/domain/usecases/expenses/get-all-project-expense-types.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    flow,
    flowResult,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

// Import the datasource for financial entities
import { ProjectFinancialEntityDataSource } from "@project/data/datasource/project-financial-entity.datasource";
import type { ProjectFinancialEntity } from "@project/domain/models/project-financial-entities.model";
import { GetAllFinancialEntitiesUseCase } from "@project/domain/usecases/financial-entity/get-all-financial-entities.usecase";
import type { FinancialEntity } from "@project/domain/models/financial-entity/financial-entities.model";

@injectable()
export class ExpensesModalBodyViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    typeSelectedId: Nullable<ExpenseTypeEnum> = null;

    @observable
    typeSelectedLabel: string = "";

    @observable
    subtypeSelectedId: Nullable<ExpenseSubTypeEnum> = null;

    @observable
    types: ExpenseType[] = [];

    @observable
    editExpenseForm: Undefinable<ExpenseAllTypes>;


    // --- Project Financial Entities State ---
    @observable
    financialEntities: ProjectFinancialEntity[] = [];

    @observable
    selectedFinancialEntityId: Nullable<number> = null;

    // --- All Financial Entities (id->name map) ---
    @observable
    financialEntityNameMap: Record<number, string> = {};

    // The projectId must be set from the parent or context
    @observable
    projectId: Nullable<number> = null;

    constructor(
        @inject(GetAllProjectExpenseTypesUseCase)
        private readonly getAllProjectExpenseTypesUseCase: GetAllProjectExpenseTypesUseCase,
        @inject(ProjectFinancialEntityDataSource)
        private readonly projectFinancialEntityDatasource: ProjectFinancialEntityDataSource,
        @inject(GetAllFinancialEntitiesUseCase)
        private readonly getAllFinancialEntitiesUseCase: GetAllFinancialEntitiesUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.initViewData();
        // No setear aquí, solo en fetchFinancialEntities tras cargar entidades
    }

    @action.bound
    async setType(type: ExpenseTypeEnum, label: string): Promise<void> {
        runInAction(() => {
            this.typeSelectedId = type;
            this.typeSelectedLabel = label;
            // Reset subtipo cuando se cambia el tipo
            this.subtypeSelectedId = null;
        });
    }

    @action.bound
    setSubType(subtype: ExpenseSubTypeEnum): void {
        runInAction(() => {
            this.subtypeSelectedId = subtype;
        });
    }

    @action.bound
    setFinancialEntityId = (id: number) => {
        this.selectedFinancialEntityId = id;
    };

    async getAllExpenseTypes(): Promise<void> {
        const types = await this.getAllProjectExpenseTypesUseCase.execute();

        runInAction(() => {
            this.types = types;
        });
    }

    @action.bound
    async fetchFinancialEntities(projectId: number): Promise<void> {
        this.financialEntities = [];
        // Solo limpiar selectedFinancialEntityId si no estamos editando
        if (!this.editExpenseForm) {
            this.selectedFinancialEntityId = null;
        }
        try {
            // Fetch project financial entities
            const result = await this.projectFinancialEntityDatasource.fetchAllBy({ projectId });
            let projectFinancialEntities: ProjectFinancialEntity[] = [];
            if (result.isRight()) {
                const entitiesObj = result.getOrThrow();
                projectFinancialEntities = entitiesObj.projectFinancialEntities;
            }
            // Fetch all financial entities (for names)
            const allFinancialEntities: FinancialEntity[] = await this.getAllFinancialEntitiesUseCase.execute();
            // Build id->name map
            const nameMap: Record<number, string> = {};
            allFinancialEntities.forEach((fe) => {
                nameMap[fe.id] = fe.name;
            });
            runInAction(() => {
                this.financialEntities = projectFinancialEntities;
                this.financialEntityNameMap = nameMap;
                // Si estamos editando, setear el id de la relación ProjectFinancialEntity (el que devuelve el backend)
                if (this.editExpenseForm) {
                    const relationId = (this.editExpenseForm as any).projectFinantialEntityId;
                    if (relationId !== undefined && relationId !== null) {
                        const found = projectFinancialEntities.find(e => e.id === relationId);
                        if (found) {
                            this.selectedFinancialEntityId = found.id;
                        } else {
                            this.selectedFinancialEntityId = null;
                        }
                    } else {
                        this.selectedFinancialEntityId = null;
                    }
                }
            });
        } catch (e) {
            runInAction(() => {
                this.financialEntities = [];
                this.financialEntityNameMap = {};
            });
        }
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        yield flowResult(this.getAllExpenseTypes());

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    // Optionally, a method to set projectId and trigger fetch
    @action.bound
    setProjectId(projectId: number) {
        this.projectId = projectId;
        this.fetchFinancialEntities(projectId);
    }
}
